import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Space, Table, Form, Input , Button, Popconfirm, message, Select, Col, Row } from 'antd';
import { DatePicker, CrmSelectBox, CompanySelectBox } from 'components/atoms'
import {  useBillPublicDetail, useRefCode } from 'hooks';
import { TableWrapper } from 'components/molecules';
import { ColumnsType } from 'antd/lib/table/interface';
import moment, { Moment } from 'moment';
import { dateFormat } from 'utils/date-utils';
import CheckInputModal from 'components/molecules/CheckInputModal';
import useStore from 'store';

export interface BillPublicModalProps extends PopupProps {
    id: number;
}

const BillPublicModal = ({ isOpen, onClose, id }: BillPublicModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
  const [companySrl, setCompanySrl] = useState<number>(0);
  const [companyName, setCompanyName] = useState<string>('');
  const [isCheckInput, setIsCheckInput] = useState<boolean>(false);
  const [dropMemo, setDropMemo] = useState<string>('');  

   /* ======================================================
   * variables
   * ====================================================== */
   const [form] = Form.useForm();
   const { TextArea } = Input;
   const auth = useStore((state) => state.auth);  

   const columns: ColumnsType<BillItemInfoDto> = [
    {
      title: '교육일',
      dataIndex: 'start_day',
      key: 'start_day',
      width: '120px',
      align: 'center',
      ellipsis: true,
    },
    {
     title: '교육장소',
     dataIndex: 'lecture_place',
     key: 'lecture_place',
     width: 'auto',
     align: 'center',
     ellipsis: true,
   },   
   {
     title: '강의구분',
     dataIndex: 'lecture_type',
     key: 'lecture_type',
     width: 'auto',
     align: 'center',
     ellipsis: true,
   },   
   {
     title: '매출',
     dataIndex: 'max_price',
     key: 'max_price',
     width: '100px',
     align: 'right',
     ellipsis: true,
     render: (max_price, record) => {
      return (
        max_price && max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }     
   }, 
   {
    title: '미발행매출',
    dataIndex: 'no_bill_price',
    key: 'no_bill_price',
    width: '100px',
    align: 'right',
    ellipsis: true,
    render: (no_bill_price, record) => {
      return (
        no_bill_price && no_bill_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }      
  },  
  {
    title: '발행금액',
    dataIndex: 'bill_pirce',
    key: 'bill_pirce',
    width: '100px',
    align: 'right',
    ellipsis: true,
    render: (bill_pirce, record) => {
      return (
        bill_pirce && bill_pirce.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }     
  },      
  ];
   /* ======================================================
   * events
   * ====================================================== */
   
  const onCloseCancle = () => {
    onClose?.(false); 
  }  

  const onDropMemoClose = (isOk, memo?) => {
    if (isOk) {
      const data = {
        id: id,
        status_type:'drop',
        memo: form.getFieldValue("memo"),
        drop_memo: memo,
        crm_srl: form.getFieldValue("crm_srl"),
        company_srl: form.getFieldValue("company_srl"),
        bill_type: form.getFieldValue("bill_type"),
        bill_day: form.getFieldValue("bill_day") ? dateFormat(form.getFieldValue("bill_day"), 'YYYY-MM-DD'):'null',
        title: form.getFieldValue("title"),
      }
      billStatusUpdate(data)    
    } 
    setIsCheckInput(false);
  }

  const onUpdateState = (status: string) => {
    if (status === 'drop') {
      setDropMemo('');
      setIsCheckInput(true);
    } else {
      const data = {
        id: id,
        status_type:status,
        memo: form.getFieldValue("memo"),
        crm_srl: form.getFieldValue("crm_srl"),
        company_srl: form.getFieldValue("company_srl"),
        bill_type: form.getFieldValue("bill_type"),
        bill_day: form.getFieldValue("bill_day") ? dateFormat(form.getFieldValue("bill_day"), 'YYYY-MM-DD'):'null',
        title: form.getFieldValue("title"),
      }
      billStatusUpdate(data) 
    }
  }

  const onUpdate = () => {
    const data = {
      id: id,
      memo: form.getFieldValue("memo"),
      crm_srl: form.getFieldValue("crm_srl"),
      company_srl: form.getFieldValue("company_srl"),
      bill_type: form.getFieldValue("bill_type"),
      bill_day: form.getFieldValue("bill_day") ? dateFormat(form.getFieldValue("bill_day"), 'YYYY-MM-DD'):'null',
      title: form.getFieldValue("title"),
    }
    billUpdate(data) 
  }

  /* ======================================================
   * hooks
   * ====================================================== */
  const { billInfo, billItems, updateSuccess, billUpdate, updateStatusSuccess, billStatusUpdate } = useBillPublicDetail(id); 
  const { refCodeList: billType } = useRefCode({field:'bill_type', status_type: 'AV'});   

  /* ======================================================
   * effects
   * ====================================================== */

  useEffect(() => {
    if (!!billInfo) {
      form.setFieldsValue({
          bill_type: billInfo.bill_type,
          bill_day: (!!billInfo.bill_day && billInfo.bill_day !== '0000-00-00') ? moment(billInfo.bill_day, "YYYY-MM-DD") : undefined,
          crm_srl: billInfo.crm_srl,
          company_srl: billInfo.company_srl, 
          memo: billInfo.memo, 
          title: billInfo.title,
      });
      setCompanySrl(billInfo.company_srl);
      setCompanyName(billInfo.company_name);      
    }     
  }, [billInfo])

  useEffect(() => {
    if (updateSuccess || updateStatusSuccess)
    onClose?.(true);     
  }, [updateSuccess, updateStatusSuccess])  

    return (
      <>
        <ModalWrapper open={isOpen} onCancel={onCloseCancle} width={850} title={billInfo?.status_type === 'drop'?'반려된 요청서':'계산서 발행 내역서'} closable>
            <Space direction="vertical" style={{ width: '100%' }}>
            <Form
                    form={form}
                    name="RefCode"
                    layout="horizontal"  
                    labelCol={{ flex: '100px' }}
                    style={{width:'100%', marginBottom:'20px'}}
                >
                    <Form.Item label={'고객사'}>
                      <CompanySelectBox required={false} form={form} company_srl={companySrl} onSelected={(value, option) => {setCompanySrl(Number(value)); setCompanyName(option.label);}} view_info/>
                    </Form.Item>   
                    <Form.Item label={'결제담당자'}>
                      <CrmSelectBox form={form} company={companySrl} company_name={companyName} view_info/>  
                    </Form.Item>                                         
                    <Row>
                      <Col span={12}>
                        <Form.Item label={'프로젝트명'}>
                            <label>{billInfo?.group_name}</label>                  
                        </Form.Item>   
                      </Col>
                      <Col span={12}>
                        <Form.Item label={'기간'}>
                            <label>{billInfo?.group_date}</label>                  
                        </Form.Item>                           
                      </Col>
                    </Row>  
                    <Row>
                      <Col span={12}>
                        <Form.Item name="bill_type" label={'발행구분'}>
                          <Select placeholder="강의구분 선택" style={{width:"150px"}}>
                          {billType?.map((item) => {
                              return (<Select.Option key={item.name} value={item.name}>{item.value}</Select.Option>)
                          })}                        
                          </Select>  
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label={'요청자'}>
                          <label>{billInfo?.who_create}</label>    
                        </Form.Item>                            
                      </Col>
                    </Row>                                                             
                    <Row>
                      <Col span={12}>
                        <Form.Item name="bill_day" label={'발행일'}>
                          <DatePicker showTime={false} format="YYYY-MM-DD" />      
                        </Form.Item>    
                      </Col>
                      <Col span={12}>
                        <Form.Item name='title' label={'과정명'}>
                            <Input placeholder='과정명을 입력하세요'/>   
                        </Form.Item>                            
                      </Col>
                    </Row>     
                    <Form.Item name='memo' label={'메모'}>
                        <TextArea rows={2} placeholder='일정 메모를 입력하세요'/>   
                    </Form.Item>   
                    {(billInfo?.status_type === 'drop') && (
                    <Form.Item label={'반려사유'}>
                        <label>{billInfo?.drop_memo}</label>
                    </Form.Item>                         
                    )}                                                          
                </Form> 

                <TableWrapper isSearch={false}>
                    <Table
                        tableLayout="fixed"
                        dataSource={billItems}
                        pagination={false}
                        columns={columns}
                        style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
                        showSorterTooltip={false}
                        scroll={{ y: 240 }}
                    />
                </TableWrapper>
                <div style={{float:"right"}}>
                  <label>발행금액: {billInfo?.bill_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}원 (vat 포함: {billInfo&&(billInfo.bill_price*1.1).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")})</label>
                </div>
                {billInfo?.status_type !== 'drop' && (
                <ButtonGroup className={'buttonsWrapper'}>
                <Button onClick={()=>{onClose?.(false)}}>닫기</Button>
                <Space>
                  {(billInfo?.status_type === 'confirm')? (
                    (auth.login?.div?.split(',').some(item => ['102', '999'].includes(item))) && (
                      <Popconfirm
                          title="결재가 승인된 항목입니다. 반려 하시겠습니까?"
                          okText="반려"
                          cancelText="취소"
                          onConfirm={() => { onUpdateState('drop')}}
                      >   
                          <Button danger>반려</Button>
                      </Popconfirm>
                    )
                  ): (billInfo?.status_type === 're-request') ? (
                    <>
                      <Button onClick={onUpdate}>저장</Button>
                      <Popconfirm
                          title="결재를 다시 발행 하시겠습니까?"
                          okText="재발행"
                          cancelText="취소"
                          onConfirm={() => { onUpdateState('request') }}
                      >   
                          <Button type="primary">재발행</Button>
                      </Popconfirm>                     
                    </>
                  ) : (
                    (auth.login?.div?.split(',').some(item => ['102', '999'].includes(item))) && (
                      <>
                        <Popconfirm
                            title="결재 요청을 재요청 하시겠습니까?"
                            okText="재요청"
                            cancelText="취소"
                            onConfirm={() => {onUpdateState('re-request')}}
                        >   
                            <Button>재요청</Button>
                        </Popconfirm> 
                        <Button onClick={onUpdate}>저장</Button>
                        <Popconfirm
                            title="결재 요청을 반려 하시겠습니까?"
                            okText="반려"
                            cancelText="취소"
                            onConfirm={() => {onUpdateState('drop')}}
                        >   
                            <Button danger>반려</Button>
                        </Popconfirm>                          
                        <Popconfirm
                            title="결재 요청을 승인 하시겠습니까?"
                            okText="승인"
                            cancelText="취소"
                            onConfirm={() => { onUpdateState('confirm') }}
                        >   
                            <Button type="primary">승인</Button>
                        </Popconfirm>                   
                      </>                      
                    )
                  )}
                </Space>
            </ButtonGroup>  
                )}
            </Space>     
        </ModalWrapper>  
        <CheckInputModal
          title="반려 사유 입력"
          memo={dropMemo}
          visible={isCheckInput}
          onOk={onDropMemoClose}
          onCancel={onDropMemoClose}
        /> 
      </>        
    )    
};

export default React.memo(BillPublicModal);    